package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.dsl.ir.model.UiIr
import top.wcpe.allincore.dsl.ir.model.overlay.OverlayDescriptor
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext

/**
 * 元素 overlay 描述符挂载协作者：对【已构建完成】的 UIElement 子树补挂 overlay 描述符。
 *
 * 纯共享叶子——只遍历已建好的元素树，不递归回 [ElementTreeBuilder.buildElement]，
 * 因此 [IrSessionAssembler]（初始 build）/ [ConditionalRecomposer]（分支重建）/
 * [DataDrivenDiffer]（数据驱动重建）三方都可静态依赖它而不引入环。
 * 仅依赖 [IrInterpreter.Context]（读取 `ir` / `evalCtx`）与 IR 模型类型。
 */
internal object ElementOverlayAttacher {

    /** 初始 build 入口：根元素首帧补挂 overlay 描述符（无 per-cell EvalContext）。 */
    fun attachElementOverlays(root: UIElement<*>, ir: UiIr) {
        if (ir.overlayDescriptors.isEmpty() || ir.elementOverlays.isEmpty()) {
            return
        }
        attachElementOverlaysDfs(root, ir, null)
    }

    /**
     * 循环槽 tooltip 修复:对数据驱动(for/source)实例化的子树补挂 overlay 描述符,
     * 并把 per-cell [EvalContext](含 cell 形参)捕获到带描述符的宿主元素,
     * 使 tooltip 内 `cell.X` 能解析到当前格。重建路径不经过初始 attachElementOverlays,故在此补。
     */
    fun attachTemplateOverlays(built: UIElement<*>, ctx: IrInterpreter.Context) {
        val ir = ctx.ir
        if (ir == null || ir.overlayDescriptors.isEmpty() || ir.elementOverlays.isEmpty()) {
            return
        }
        attachElementOverlaysDfs(built, ir, ctx.evalCtx)
    }

    /**
     * @param capturedCtx 数据驱动实例化时传入的 per-cell EvalContext;静态初始 build 传 null。
     *                    非 null 时在带描述符的宿主元素上捕获它,供 overlay 渲染优先取用。
     */
    @Suppress("NestedBlockDepth")
    private fun attachElementOverlaysDfs(element: UIElement<*>, ir: UiIr, capturedCtx: EvalContext?) {
        if (element.id() != null) {
            val indexes: List<Int>? = ir.elementOverlays[element.id()]
            if (indexes != null) {
                for (index in indexes) {
                    if (index >= 0 && index < ir.overlayDescriptors.size) {
                        val descriptor: OverlayDescriptor = ir.overlayDescriptors[index]
                        // 幂等:初始 build 的 attachElementOverlays 与数据驱动实例化路径
                        // 可能都覆盖到同一元素(for 子元素首帧),避免同一描述符重复挂载。
                        if (!element.overlayDescriptors().contains(descriptor)) {
                            element.overlayDescriptor(descriptor)
                        }
                    }
                }
                // P2a:数据驱动实例化时把 per-cell EvalContext 捕获到 tooltip 宿主元素。
                // 仅在尚未捕获时设置:嵌套(如 if 包 for)时内层 for 先捕获带循环变量的 ctx,
                // 外层 $cond / for 的 attach 不得用更外层(缺循环变量)的 ctx 覆盖它——否则 cell.X 丢失。
                if (capturedCtx != null && element.capturedEvalContext() == null) {
                    element.capturedEvalContext(capturedCtx)
                }
            }
        }
        for (child in element.children()) {
            attachElementOverlaysDfs(child, ir, capturedCtx)
        }
    }
}
